import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid, type DataGridHandle } from "../../components/dataGrid/dataGrid.tsx";
import { useSession } from "../../hooks/useSession.ts";
import { useToast } from "../../hooks/useToast.ts";
import {
  saveAdvancedPriceData, getLocationProductKioskDivergence, type KioskDivergenceRow,
} from "../../api/price.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { fmtPrice } from "../../util/pricingCascade.ts";
import { ParamType } from "../../types/grid.ts";
import type { ProductCacheItem } from "../../types/session.ts";
import {
  hiddenPriceColumns, PRICE_COLUMNS, PRICE_COLUMN_TO_TYPE, CATEGORY_COLUMNS, PRICE_TAX_PAIRS, calcTaxPairs,
  type PendingEdits, type PriceCategory,
} from "./advancedPriceShared.ts";
import {
  buildPriceTabGridOverride, buildPriceTabSaveItems, type PriceTabScope,
} from "./priceTabGridOverride.tsx";
import { loadPriceTaxMode, PRICETAX_LS_MODE, PRICETAX_LS_RATE } from "./priceTaxEntry.tsx";
import type { PriceTaxMode } from "../../util/priceTaxDerive.ts";
import formStyles from "../../styles/formPage.module.scss";
import styles from "./advancedPriceGridPage.module.scss";

/** Friendly column headers per price type — matches the Advanced grid's labels. */
const PT_LABEL: Record<number, string> = {
  1: "Price", 2: "Tax", 3: "Rental Price", 4: "Rental Tax Rate", 5: "EH Price", 6: "EH Tax",
  7: "Reserve · Late", 8: "Reserve · Late Tax Rate", 9: "Reserve · Period",
  11: "Adv Tax 1", 12: "Adv Tax 2", 13: "Adv Tax 3", 14: "Adv Tax 4", 15: "Adv Tax 5",
};

const overrideCol = (pt: number) => `Override${pt}`;

interface PriceTabGridProps {
  gridName: string;
  scope: PriceTabScope;
  /** Page-def function-parameter / WHERE column the kiosk/location id binds to. */
  scopeParamColumn: string;
  scopeParamValue: number;
  onReady?: () => void;
}

export function PriceTabGrid(props: PriceTabGridProps) {
  const { cache } = useSession();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const pendingRef = useRef<PendingEdits>(new Map());
  const gridRef = useRef<DataGridHandle | null>(null);

  // Location-only DiffCount drill-down state + per-product cache.
  const [diverge, setDiverge] = useState<{ productId: number; rows: KioskDivergenceRow[] } | null>(null);
  const [divergeLoading, setDivergeLoading] = useState(false);
  const [divergeError, setDivergeError] = useState("");
  const divergeReqRef = useRef(0); // guards against stale/out-of-order divergence fetches

  const isLocation = props.scope.kind === "location";

  // Show one price category at a time, mirroring the Advanced grid. Options gated by
  // DB feature flags (Buy always on); columns outside the active category are hidden.
  const categoryOptions = useMemo(() => {
    const f = getDbFeatureFlags(cache);
    return ([
      { key: "normal" as const, label: "Buy", show: true },
      { key: "eathere" as const, label: "Eat Here", show: !!f.ProductShowEatHere },
      { key: "rental" as const, label: "Rental", show: !!f.ProductShowRental },
      { key: "reserve" as const, label: "Reserve", show: !!f.ProductShowReserve },
    ]).filter((c) => c.show);
  }, [cache]);
  const [category, setCategory] = useState<PriceCategory>("normal");
  const activeCategory: PriceCategory = categoryOptions.some((c) => c.key === category) ? category : "normal";

  // Entry mode (Direct / Net+rate / Gross+rate) + rate, shared with the rest of the
  // price UI via localStorage. In a calc mode each price cell becomes a net/gross
  // editor and its paired tax column is hidden.
  const [priceMode, setPriceMode] = useState<PriceTaxMode>(loadPriceTaxMode);
  const [priceRate, setPriceRate] = useState(() => localStorage.getItem(PRICETAX_LS_RATE) || "25%");
  useEffect(() => { localStorage.setItem(PRICETAX_LS_MODE, priceMode); }, [priceMode]);
  useEffect(() => { localStorage.setItem(PRICETAX_LS_RATE, priceRate); }, [priceRate]);
  const calcMode = priceMode !== "direct";
  // When the Late Taxrate is hidden, calc mode must not fold/expose it (PT7 stays direct).
  const hideLateTaxRate = useMemo(() => !!getDbFeatureFlags(cache).ProductHideReserveLateTaxrate, [cache]);
  const ENTRY_MODES = [
    ["direct", "Direct"], ["net", "Net + rate"], ["gross", "Gross + rate"],
  ] as const;

  const productMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of (cache?.CommonData?.Product ?? []) as ProductCacheItem[]) m.set(p.ProductID, p.Name ?? "");
    return m;
  }, [cache]);

  const handleDiffClick = useCallback((productId: number) => {
    if (props.scope.kind !== "location") return;
    const locationId = props.scope.locationId;
    const reqId = ++divergeReqRef.current; // ignore resolutions superseded by a newer click
    setDiverge({ productId, rows: [] });
    setDivergeError("");
    setDivergeLoading(true);
    getLocationProductKioskDivergence(locationId, productId)
      .then((rows) => { if (divergeReqRef.current === reqId) setDiverge({ productId, rows }); })
      .catch((e) => { if (divergeReqRef.current === reqId) setDivergeError(String(e)); })
      .finally(() => { if (divergeReqRef.current === reqId) setDivergeLoading(false); });
  }, [props.scope]);

  const override = useMemo(
    () => buildPriceTabGridOverride(
      props.scope, pendingRef, bump, productMap,
      isLocation ? handleDiffClick : undefined,
      priceMode, priceRate, hideLateTaxRate,
    ),
    [props.scope, bump, productMap, isLocation, handleDiffClick, priceMode, priceRate, hideLateTaxRate],
  );

  const staticFilters = useMemo(
    () => [{ sColumn: props.scopeParamColumn, Type: ParamType.Specified, oValue: String(props.scopeParamValue) }],
    [props.scopeParamColumn, props.scopeParamValue],
  );

  // Hide override columns for disabled feature-flag types + internal raw
  // columns; the DiffCount column is location-only.
  const hiddenColumns = useMemo(() => {
    const hiddenTypes = new Set(
      hiddenPriceColumns(getDbFeatureFlags(cache)).map((c) => PRICE_COLUMN_TO_TYPE[c]),
    );
    // Also hide every override column outside the active category (Buy/Eat Here/Rental/Reserve).
    const inCategory = new Set(CATEGORY_COLUMNS[activeCategory].map((c) => PRICE_COLUMN_TO_TYPE[c]));
    const hidden = PRICE_COLUMNS
      .filter((c) => hiddenTypes.has(PRICE_COLUMN_TO_TYPE[c]) || !inCategory.has(PRICE_COLUMN_TO_TYPE[c]))
      .map((c) => overrideCol(PRICE_COLUMN_TO_TYPE[c]!));
    // NOT hiding SourceWeight: it stays a visible column relabelled "Source" and is
    // painted by the decorator (describeWeight). hiddenColumns drops a column from
    // the grid entirely (extraColumnInfo force-show only affects the filter bar),
    // so hiding it would make the Source column disappear.
    // In a calc mode the paired tax override columns are folded into the price cell.
    if (calcMode) for (const p of PRICE_TAX_PAIRS) hidden.push(overrideCol(p.taxType));
    const internal = ["ProductRef"];
    if (!isLocation) internal.push("DiffCount");
    return [...hidden, ...internal];
  }, [cache, isLocation, activeCategory, calcMode]);

  const extraColumnInfo = useMemo(() => {
    const cols: { ColumnName: string; Header?: string; DisplayType?: string }[] = [
      { ColumnName: "Product", Header: "Product" },
      { ColumnName: "Price", Header: "Current Price" },
      { ColumnName: "Tax", Header: "Current Tax" },
      // "Source" = the SourceWeight column relabelled; the decorator paints it via
      // describeWeight (e.g. "Product + Location"). Stays visible (not hidden).
      { ColumnName: "SourceWeight", Header: "Source" },
      { ColumnName: "DiffCount", Header: "Kiosks differ" },
    ];
    const folded = calcTaxPairs(hideLateTaxRate);
    const ph = (pt: number, base: string) =>
      calcMode && (folded.some((p) => p.priceType === pt) || pt === 9)
        ? `${base}${priceMode === "gross" ? " (total)" : " (net)"}`
        : base;
    for (const c of PRICE_COLUMNS) {
      const pt = PRICE_COLUMN_TO_TYPE[c]!;
      cols.push({ ColumnName: overrideCol(pt), Header: ph(pt, PT_LABEL[pt] ?? c), DisplayType: "TEXTBOX" });
    }
    return cols;
  }, [calcMode, priceMode, hideLateTaxRate]);

  const pendingCount = pendingRef.current.size;

  const handleSave = useCallback(async () => {
    const items = buildPriceTabSaveItems(props.scope, pendingRef.current);
    if (items.length === 0) return;
    setSaving(true);
    try {
      await saveAdvancedPriceData(items);
      toast("Price overrides saved.", "success");
      pendingRef.current = new Map();
      bump();
      gridRef.current?.refresh();
    } catch (err) {
      toast(String(err), "error");
    } finally {
      setSaving(false);
    }
  }, [props.scope, toast, bump]);

  const discard = useCallback(() => { pendingRef.current = new Map(); bump(); }, [bump]);

  // Fire onReady once after the first data load (DataGrid.onDataLoaded fires on
  // every load; the parent tab only needs the first "tab is populated" signal).
  const readyFiredRef = useRef(false);
  const handleDataLoaded = useCallback(() => {
    if (readyFiredRef.current) return;
    readyFiredRef.current = true;
    props.onReady?.();
  }, [props]);

  return (
    <div>
      <div className={styles.toolbar}>
        <span className={styles.modeLabel}>Entry</span>
        <div className={styles.modeGroup} role="tablist" title="How price/tax is entered. Net/Gross hide the tax column and compute from the rate.">
          {ENTRY_MODES.map(([m, label]) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={priceMode === m}
              className={priceMode === m ? styles.modeBtnActive : styles.modeBtn}
              onClick={() => setPriceMode(m)}
            >
              {label}
            </button>
          ))}
        </div>
        {calcMode && (
          <input
            className={styles.rateInput}
            type="text"
            inputMode="decimal"
            value={priceRate}
            onChange={(e) => setPriceRate(e.target.value)}
            placeholder="25%"
            title="Tax rate applied to every calc-mode edit on this tab"
          />
        )}
        {categoryOptions.length > 1 && (
          <>
            <span className={styles.modeLabel}>Show</span>
            <div className={styles.modeGroup} role="radiogroup" aria-label="Price category" title="Which price category to edit. Reserve also surfaces the Rental Price + Rental Tax Rate it reuses.">
              {categoryOptions.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={activeCategory === c.key}
                  className={activeCategory === c.key ? styles.modeBtnActive : styles.modeBtn}
                  onClick={() => setCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </>
        )}
        <div className={styles.spacer} />
        {pendingCount > 0 && (
          <>
            <button className={formStyles.secondaryBtn} onClick={discard} disabled={saving}>Discard</button>
            <button className={formStyles.primaryBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : `Save ${pendingCount} change${pendingCount === 1 ? "" : "s"}`}
            </button>
          </>
        )}
      </div>

      <DataGrid
        ref={(h) => { gridRef.current = h; }}
        name={props.gridName}
        staticFilters={staticFilters}
        hiddenColumns={hiddenColumns}
        extraColumnInfo={extraColumnInfo}
        editable
        autoSearch
        gridOverride={override}
        onDataLoaded={handleDataLoaded}
      />

      {isLocation && diverge && (
        <DivergencePanel
          productName={productMap.get(diverge.productId) ?? `#${diverge.productId}`}
          rows={diverge.rows}
          loading={divergeLoading}
          error={divergeError}
          onClose={() => setDiverge(null)}
        />
      )}
    </div>
  );
}

/** Inline panel listing the location's kiosks whose actual price diverges from
 *  the location-effective price for one product. */
function DivergencePanel(props: {
  productName: string;
  rows: KioskDivergenceRow[];
  loading: boolean;
  error: string;
  onClose: () => void;
}) {
  return (
    <div className={styles.divergePanel}>
      <div className={styles.divergeHeader}>
        <span>Kiosks differing on <strong>{props.productName}</strong></span>
        <button className={styles.changeCancelBtn} title="Close" onClick={props.onClose}>✕</button>
      </div>
      {props.loading && <p className={styles.divergeStatus}>Loading…</p>}
      {props.error && <p className={styles.divergeStatus}>{props.error}</p>}
      {!props.loading && !props.error && props.rows.length === 0 && (
        <p className={styles.divergeStatus}>No divergent kiosks.</p>
      )}
      {!props.loading && !props.error && props.rows.length > 0 && (
        <div className={styles.divergeList}>
          {props.rows.map((d) => (
            <div key={d.KioskID} className={styles.divergeItem}>
              <span className={styles.divergeKiosk}>Kiosk #{d.KioskID}</span>
              <span className={styles.divergeMeta}>
                {Object.entries(d.OverrideValues ?? {})
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([pt, v]) => `${PT_LABEL[Number(pt)] ?? `#${pt}`}: ${fmtPrice(v)}`)
                  .join(" · ") || "no overrides"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
